import isAppointment from '../adressHelpers/taskKind'
import materialQuantities from '../materialsHelpers/materialQuantities'
import { readRegistrationDate } from '../materialsHelpers/registrationDate'

const DEFAULT_LIMIT = 8

const safeArray = (value) => Array.isArray(value) ? value : []

const isDeleted = (doc) => doc?.Deleted === true || doc?.Deleted === 'true'

const normalizeText = (value) => String(value || '').trim()

const getTimestampDate = (timestamp) => {
    if (!timestamp) return null
    if (typeof timestamp.toDate === 'function') return timestamp.toDate()
    const date = new Date(timestamp)
    return Number.isNaN(date.getTime()) ? null : date
}

const isInRange = (timestamp, range) => {
    const start = range?.start ? new Date(`${range.start}T00:00:00`) : null
    const end = range?.end ? new Date(`${range.end}T23:59:59.999`) : null
    if (!start && !end) return true

    const date = getTimestampDate(timestamp)
    if (!date) return false
    if (start && date < start) return false
    if (end && date > end) return false
    return true
}

const sortRows = (rows, limit = DEFAULT_LIMIT) => rows
    .slice()
    .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count
        return String(a.label).localeCompare(String(b.label), 'nl-NL')
    })
    .slice(0, Math.max(1, Number(limit) || DEFAULT_LIMIT))

/**
 * @param {{
 *   entries?: Array<{MaterialID?: string, Quantity?: number, Status?: string, RegisteredAt?: any, Timestamp?: any, Deleted?: boolean|string}>,
 *   materials?: Array<{ID?: string, Title?: string, Category?: string}>,
 *   range?: {start?: string|null, end?: string|null},
 *   groupBy?: 'material'|'category',
 *   statusFilter?: 'all'|'done'|'toDo',
 *   limit?: number,
 * }} input
 * @returns {Array<{id: string, label: string, count: number}>}
 */
const aggregateMeasuresByType = ({ entries = [], materials = [], range = null, groupBy = 'material', statusFilter = 'done', limit = DEFAULT_LIMIT } = {}) => {
    const materialsById = new Map()
    safeArray(materials).forEach((material) => {
        if (material?.ID) materialsById.set(material.ID, material)
    })

    const totals = new Map()
    safeArray(entries).forEach((entry) => {
        if (!entry || isDeleted(entry)) return
        // Status en aantal uit materialQuantities: een regel met 'Gebruikt' is
        // 'done' ook zonder Status-veld. AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10)
        const aantallen = materialQuantities(entry)
        if (statusFilter !== 'all' && aantallen.status !== statusFilter) return
        // De registratiedatum, niet `Timestamp` (laatst gewijzigd): anders
        // verhuist een aangepaste regel naar de periode van de aanpassing.
        // Een rij van vóór het datumveld valt terug op Timestamp (bug 9).
        // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T22)
        if (!isInRange(readRegistrationDate(entry).ms, range)) return

        const material = materialsById.get(entry.MaterialID)
        const fallback = material?.Title || 'Onbekend materiaal'
        const label = groupBy === 'category'
            ? normalizeText(material?.Category) || fallback
            : fallback
        const id = groupBy === 'category'
            ? `category:${label.toLowerCase()}`
            : entry.MaterialID || `unknown:${label.toLowerCase()}`
        const quantity = aantallen.impact
        totals.set(id, {
            id,
            label,
            count: (totals.get(id)?.count || 0) + quantity,
        })
    })

    return sortRows(Array.from(totals.values()), limit)
}

const resolveArea = (address, groupBy) => {
    const location = address?.Location || {}
    if (groupBy === 'wijk') {
        const code = normalizeText(location.WijkCode)
        return {
            id: code || normalizeText(location.Wijk || location.WijkNaam) || 'onbekend',
            label: normalizeText(location.Wijk || location.WijkNaam) || code || 'Wijk onbekend',
        }
    }
    if (groupBy === 'buurt') {
        const code = normalizeText(location.BuurtCode)
        return {
            id: code || normalizeText(location.Buurt || location.BuurtNaam) || 'onbekend',
            label: normalizeText(location.Buurt || location.BuurtNaam) || code || 'Buurt onbekend',
        }
    }
    const postcode = normalizeText(location.Postcode || address?.Postcode)
        .replace(/\s+/g, '')
        .toUpperCase()
    const postcode6 = postcode || 'ONBEKEND'
    return { id: postcode6, label: postcode ? postcode6 : 'Postcode onbekend' }
}

/**
 * @param {{
 *   addresses?: Array<{ID?: string, Status?: string, Timestamp?: any, Location?: object, Postcode?: string}>,
 *   tasks?: Array<{AdressID?: string, Timestamp?: any, Type?: string}>,
 *   range?: {start?: string|null, end?: string|null},
 *   groupBy?: 'postcode6'|'wijk'|'buurt',
 *   countBy?: 'addresses'|'visits',
 *   onlyCompleted?: boolean,
 *   limit?: number,
 * }} input
 * @returns {Array<{id: string, label: string, count: number}>}
 */
const aggregateHouseVisitsByArea = ({ addresses = [], tasks = [], range = null, groupBy = 'postcode6', countBy = 'addresses', onlyCompleted = true, limit = DEFAULT_LIMIT } = {}) => {
    const addressesById = new Map()
    safeArray(addresses).forEach((address) => {
        if (address?.ID && !isDeleted(address)) addressesById.set(address.ID, address)
    })

    const source = countBy === 'visits'
        ? safeArray(tasks)
            // isAppointment, niet Type: een subtaak is geen bezoek.
            // AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md
            .filter((task) => isAppointment(task) && isInRange(task.Timestamp, range))
            .map((task) => addressesById.get(task.AdressID))
            .filter(Boolean)
        : safeArray(addresses).filter((address) => {
            if (!address || isDeleted(address)) return false
            if (onlyCompleted && address.Status !== 'completed') return false
            return isInRange(address.Timestamp, range)
        })

    const totals = new Map()
    source.forEach((address) => {
        const area = resolveArea(address, groupBy)
        totals.set(area.id, {
            id: area.id,
            label: area.label,
            count: (totals.get(area.id)?.count || 0) + 1,
        })
    })

    return sortRows(Array.from(totals.values()), limit)
}

export {
    aggregateHouseVisitsByArea,
    aggregateMeasuresByType,
    isInRange,
}
